import { ChartData, ChartDataset } from 'chart.js';

import APIError from 'types/api_error';
import { DimensionInput } from 'dashboards/types/dimension';
import { FilterInput } from 'dashboards/types/filter';
import { RequireField } from 'types/utility_types';
import { TimeRangeInput, TimeSeriesGranularity } from 'dashboards/types/time';

import { hslaToString, MAIN_CHART_COLOR } from 'dashboards/services/color';
import { useChartColors } from 'dashboards/hooks/useChartColors';
import { useTimeSeries } from 'dashboards/hooks/useTimeSeries';

type UseGroupedBarChartParams = {
  metricName: string;
  granularity: TimeSeriesGranularity;
  timeRange: TimeRangeInput;
  dimensions: Array<DimensionInput>;
  filters: Array<FilterInput>;
  timeZone: string;
};

type DataSetType = Array<ChartDataset<'bar', number[]> & { label: string }>;
type DataType = Omit<
  RequireField<ChartData<'bar', number[], string>, 'labels'>,
  'datasets'
> & { datasets: DataSetType };

type GroupedBarChartResult = {
  data?: DataType;
  isLoading: boolean;
  error?: APIError | null;
};

export function useGroupedBarChart(
  params: UseGroupedBarChartParams,
  variant: 'stacked' | 'grouped'
): GroupedBarChartResult {
  const {
    data,
    isLoading: isLoadingTimeSeries,
    error,
  } = useTimeSeries({ params });

  const { data: colors, isLoading: isLoadingColors } = useChartColors(
    data?.map(({ labels, values, ...headers }) =>
      String(Object.values(headers)[0])
    )
  );

  const isLoading = isLoadingTimeSeries || isLoadingColors;

  if (!data || !colors) {
    return { isLoading, error };
  }

  // FIXME Remove this once fixed in api-server
  if (data.length === 0) {
    return {
      isLoading: false,
      error: { message: 'No data available yet.', code: '', status: 0 },
    };
  }

  const [{ labels }] = data;
  const datasets = data.map(({ values, labels, ...headers }, index) => {
    const [label] = Object.values(headers);
    return {
      label: String(label),
      backgroundColor: hslaToString(colors[index] ?? MAIN_CHART_COLOR),
      borderRadius: 2,
      data: values,
      stack: variant === 'stacked' ? 'stack0' : String(label),
    };
  });

  return {
    data: { labels, datasets },
    isLoading,
    error,
  };
}
